/**
 * Functions for determining tile format based on asset type and render hints
 */

import { AssetResponse, isSportsEvent } from "../FetchAssetDetails/types";
import { TileFormat } from "./types";

import { PeacockNodeType } from "@/utils/shared-atom-types";
import { isLink } from "@/utils/FetchAssetDetails/types";

/**
 * Determines the appropriate TileFormat based on asset type and render hints
 *
 * @remarks
 * This function evaluates the asset and its render hints to determine the correct tile format:
 * - **Live**: When renderHint.liveIndicator is present
 * - **Collection**: For CATALOGUE/COLLECTION assets or CATALOGUE/LINK with default catalogueType
 * - **Hub**: For CATALOGUE/HUB assets or CATALOGUE/LINK with catalogueType "HUB"
 * - **Clip**: For ASSET/CLIP assets
 * - **VOD**: Default format for movies, shows, episodes, and other standard content
 * - **Replay**: For SLEs marked with eventStage "REPLAY"
 * - **Upcoming**: For SLEs marked with eventStage "UPCOMING"
 *
 * The function checks render hints first (for Live indicator), then evaluates the asset type
 * to determine the appropriate tile format for rendering.
 *
 * @param asset - The asset response object containing type and attributes
 * @param renderHint - Optional render hint object that may contain display indicators like liveIndicator
 * @returns The appropriate TileFormat enum value for rendering the tile
 *
 * @example
 * ```typescript
 * // Standard VOD asset (movie or show)
 * const vodFormat = getTileFormat(movieAsset);
 * // Returns: TileFormat.VOD
 *
 * // Live asset with render hint
 * const liveFormat = getTileFormat(liveAsset, { liveIndicator: true });
 * // Returns: TileFormat.Live
 *
 * // Collection asset
 * const collectionFormat = getTileFormat(collectionAsset);
 * // Returns: TileFormat.Collection
 *
 * // Hub link asset
 * const hubFormat = getTileFormat(linkAsset);
 * // Returns: TileFormat.Hub (if catalogueType is "HUB")
 * ```
 */
const getTileFormat = (asset: AssetResponse, renderHint?: any): TileFormat => {
  /** Check render hints first **/
  if (renderHint?.liveIndicator) {
    return TileFormat.Live;
  }

  /** Check asset type **/
  const assetType = asset.type;

  if (assetType === PeacockNodeType.Collection) {
    return TileFormat.Collection;
  }

  if (assetType === PeacockNodeType.Hub) {
    return TileFormat.Hub;
  }

  /** Handle CATALOGUE/LINK - check the catalogueType in attributes or linkInfo **/
  if (assetType === PeacockNodeType.Link && isLink(asset)) {
    const catalogueType = asset.attributes.catalogueType;
    if (catalogueType === "HUB") {
      return TileFormat.Hub;
    }
    /** Default to Collection for other CATALOGUE/LINK types **/
    return TileFormat.Collection;
  }

  if (assetType === PeacockNodeType.Clip) {
    return TileFormat.Clip;
  }

  /** Handle different types of SLEs based on eventStage **/
  if (isSportsEvent(asset)) {
    const eventStage = asset.attributes.eventDetails.eventStage;

    switch (eventStage) {
      case "REPLAY":
        return TileFormat.Replay;
      case "UPCOMING":
        return TileFormat.Upcoming;
      case "LIVE":
        return TileFormat.Live;
      default:
        return TileFormat.VOD;
    }
  }

  /** Default to VOD for movies, shows, episodes **/
  return TileFormat.VOD;
};

export { getTileFormat };
